"use client";

import { AIChatInputContext, type AIChatInputContextValue } from "../contexts";
import { useAIChatContext } from "../contexts";
import { useCallback, useMemo, useRef, useState } from "react";
interface AIChatInputProps {
  children: React.ReactNode;
  className?: string;
  /** Keep drafts while a server-owned action must be resolved first. */
  disabled?: boolean;
}

/**
 * Compound component root for the AI chat input area.
 * Provides AIChatInputContext so children (TextArea, FileUpload, SendButton, etc.)
 * can communicate through shared state.
 */
export function AIChatInput({
  children,
  className,
  disabled = false
}: AIChatInputProps) {
  const {
    state: chatState,
    actions: chatActions
  } = useAIChatContext();
  const [text, setText] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const textRef = useRef(text);
  textRef.current = text;
  const filesRef = useRef(files);
  filesRef.current = files;
  const addFiles = useCallback((newFiles: File[]) => {
    setFiles(prev => [...prev, ...newFiles]);
  }, []);
  const removeFile = useCallback((index: number) => {
    setFiles(prev_0 => prev_0.filter((_, i) => i !== index));
  }, []);
  const send = useCallback(() => {
    const currentText = textRef.current.trim();
    const currentFiles = filesRef.current;
    if (!currentText && currentFiles.length === 0) return;
    if (chatState.isStreaming || disabled) return;
    chatActions.sendMessage(currentText, currentFiles.length > 0 ? currentFiles : undefined);
    setText("");
    setFiles([]);
  }, [chatState.isStreaming, chatActions, disabled]);
  const cancel = useCallback(() => {
    if (!chatState.isStopping) chatActions.cancel();
  }, [chatActions, chatState.isStopping]);
  const isDisabled = disabled || !text.trim() && files.length === 0;
  const value = useMemo<AIChatInputContextValue>(() => ({
    state: {
      text,
      files,
      isStreaming: chatState.isStreaming,
      isStopping: chatState.isStopping,
      isDisabled
    },
    actions: {
      setText,
      addFiles,
      removeFile,
      send,
      cancel
    }
  }), [text, files, chatState.isStreaming, chatState.isStopping, isDisabled, addFiles, removeFile, send, cancel]);
  return <AIChatInputContext value={value}>
      <div className={className}>{children}</div>
    </AIChatInputContext>;
}